import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  Edit,
  Trash2,
  Star,
  Loader2,
  Users,
  MessageSquare,
  Shield,
  Clock,
  Check,
  X,
  CreditCard,
  PoundSterling,
  RotateCcw,
  Settings,
  AlertTriangle,
  Banknote,
  Scale,
  CheckCircle,
  XCircle,
  Unlock,
  Lock,
  PlayCircle,
  UserPlus
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/100x100?text=No+Photo';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const initialTrainerForm = {
  name: '',
  photo_url: '',
  location: '',
  bio: '',
  specialties: '',
  whatsapp: '',
  instagram: ''
};

export default function Admin() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [trainers, setTrainers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [pendingTrainers, setPendingTrainers] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [trainerPayouts, setTrainerPayouts] = useState([]);
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [sessionAttendance, setSessionAttendance] = useState(null);
  const [leadUnlocks, setLeadUnlocks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false);
  const [isAdminUnlockDialogOpen, setIsAdminUnlockDialogOpen] = useState(false);
  const [adminUnlockForm, setAdminUnlockForm] = useState({ trainer_id: '', user_id: '', reason: '' });
  const [trainersForUnlock, setTrainersForUnlock] = useState([]);
  const [usersForUnlock, setUsersForUnlock] = useState([]);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [formData, setFormData] = useState(initialTrainerForm);
  const [pricingForm, setPricingForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [processingPayouts, setProcessingPayouts] = useState(false);
  const [creatingDemo, setCreatingDemo] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
      fetchPaymentData();
      fetchPricing();
      fetchTrainerPayouts();
      fetchFlaggedUsers();
      fetchDisputes();
      fetchLeadUnlocks();
      fetchPayoutStatus();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    try {
      const [trainersRes, reviewsRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/trainers`),
        axios.get(`${API_URL}/admin/reviews`),
        axios.get(`${API_URL}/admin/pending-trainers`)
      ]);
      setTrainers(trainersRes.data);
      setReviews(reviewsRes.data);
      setPendingTrainers(pendingRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentData = async () => {
    try {
      const [summaryRes, bookingsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/payments/summary`),
        axios.get(`${API_URL}/admin/payments/bookings?limit=50`)
      ]);
      setPaymentSummary(summaryRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error fetching payment data:', error);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API_URL}/payments/pricing`);
      setPricing(response.data);
      setPricingForm({
        session_30min: response.data.session_prices.session_30min.price,
        session_60min: response.data.session_prices.session_60min.price,
        session_90min: response.data.session_prices.session_90min.price,
        lead_unlock: response.data.trainer_fees.lead_unlock.price,
        premium_monthly: response.data.trainer_fees.premium_monthly.price,
        platform_commission_percent: response.data.platform_commission
      });
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const fetchTrainerPayouts = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/trainers/payout-status`);
      setTrainerPayouts(response.data);
    } catch (error) {
      console.error('Error fetching trainer payouts:', error);
    }
  };

  const fetchFlaggedUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/fraud/flagged-users`);
      setFlaggedUsers(response.data);
    } catch (error) {
      console.error('Error fetching flagged users:', error);
    }
  };

  const fetchDisputes = async () => {
    try {
      const [disputesRes, attendanceRes] = await Promise.all([
        axios.get(`${API_URL}/admin/disputes`),
        axios.get(`${API_URL}/admin/sessions/attendance`)
      ]);
      setDisputes(disputesRes.data.disputes || []);
      setSessionAttendance(attendanceRes.data);
    } catch (error) {
      console.error('Error fetching disputes:', error);
    }
  };

  const fetchLeadUnlocks = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/lead-unlocks`);
      setLeadUnlocks(response.data);
    } catch (error) {
      console.error('Error fetching lead unlocks:', error);
    }
  };

  const fetchTrainersAndUsersForUnlock = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/trainers-users`);
      setTrainersForUnlock(response.data.trainers || []);
      setUsersForUnlock(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch trainers/users:', error);
    }
  };

  const handleAdminUnlock = async () => {
    if (!adminUnlockForm.trainer_id || !adminUnlockForm.user_id) {
      toast.error('Please select both trainer and user');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/admin/unlock-client`, adminUnlockForm);
      if (response.data.already_unlocked) {
        toast.info('Client was already unlocked for this trainer');
      } else {
        toast.success('Client unlocked successfully (fee bypassed)');
      }
      setIsAdminUnlockDialogOpen(false);
      setAdminUnlockForm({ trainer_id: '', user_id: '', reason: '' });
      fetchLeadUnlocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolveDispute = async (disputeId, resolution) => {
    try {
      await axios.post(`${API_URL}/admin/disputes/${disputeId}/resolve`, {
        booking_id: disputeId,
        resolution: resolution
      });
      toast.success('Dispute resolved');
      fetchDisputes();
      fetchPaymentData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve dispute');
    }
  };

  const handleRefundBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to refund this booking?')) return;
    try {
      await axios.post(`${API_URL}/admin/payments/refund`, { booking_id: bookingId });
      toast.success('Booking refunded');
      fetchPaymentData();
    } catch (error) {
      toast.error('Failed to refund booking');
    }
  };

  const handleRefund = async (transactionId) => {
    if (!window.confirm('Are you sure you want to refund this transaction?')) return;
    try {
      await axios.post(`${API_URL}/admin/payments/refund`, { transaction_id: transactionId });
      toast.success('Transaction refunded');
      fetchPaymentData();
    } catch (error) {
      toast.error('Failed to refund transaction');
    }
  };

  const handleTriggerPayout = async (bookingId) => {
    if (!window.confirm('Trigger payout for this booking?')) return;
    try {
      const response = await axios.post(`${API_URL}/admin/payments/trigger-payout/${bookingId}`);
      toast.success(`Payout sent: £${response.data.amount}`);
      fetchPaymentData();
      fetchTrainerPayouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger payout');
    }
  };

  const handleUpdatePricing = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/admin/pricing`, pricingForm);
      toast.success('Pricing updated');
      setIsPricingDialogOpen(false);
      fetchPricing();
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnflagUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/admin/fraud/unflag/${userId}`);
      toast.success('User unflagged');
      fetchFlaggedUsers();
    } catch (error) {
      toast.error('Failed to unflag user');
    }
  };

  const handleProcessPayouts = async () => {
    setProcessingPayouts(true);
    try {
      const response = await axios.post(`${API_URL}/admin/payments/process-payouts`);
      const results = response.data;
      toast.success(`Processed ${results.eligible_processed?.successful || 0} payouts, ${results.retries?.successful || 0} retries`);
      fetchPaymentData();
      fetchTrainerPayouts();
      fetchPayoutStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payouts');
    } finally {
      setProcessingPayouts(false);
    }
  };

  const fetchPayoutStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/payments/payout-status`);
      setPayoutStatus(response.data);
    } catch (error) {
      console.error('Error fetching payout status:', error);
    }
  };

  const handleCreateDemoAccount = async () => {
    if (!window.confirm('Create/reset the demo trainer account? This will overwrite any existing demo data.')) return;
    setCreatingDemo(true);
    try {
      const response = await axios.post(`${API_URL}/admin/setup-demo-account`);
      toast.success('Demo account created!');
      toast((t) => (
        <div>
          <p className="font-semibold">Demo Account Ready</p>
          <p className="text-sm">Email: {response.data.demo_login.email}</p>
          <p className="text-sm">Password: {response.data.demo_login.password}</p>
        </div>
      ), { duration: 10000 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create demo account');
    } finally {
      setCreatingDemo(false);
    }
  };

  const handleClearTestData = async () => {
    if (!window.confirm('Are you sure you want to delete ALL trainers and reviews? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/admin/clear-test-data`);
      toast.success('Test data cleared');
      fetchData();
    } catch (error) {
      toast.error('Failed to clear test data');
    }
  };

  const handleApproveTrainer = async (applicationId) => {
    try {
      await axios.post(`${API_URL}/admin/pending-trainers/${applicationId}/approve`);
      toast.success('Trainer approved successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve trainer');
    }
  };

  const handleRejectTrainer = async (applicationId) => {
    if (!window.confirm('Are you sure you want to reject this application?')) return;
    try {
      await axios.delete(`${API_URL}/admin/pending-trainers/${applicationId}/reject`);
      toast.success('Application rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject application');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenDialog = (trainer = null) => {
    if (trainer) {
      setEditingTrainer(trainer);
      setFormData({
        name: trainer.name,
        photo_url: trainer.photo_url,
        location: trainer.location,
        bio: trainer.bio,
        specialties: trainer.specialties.join(', '),
        whatsapp: trainer.whatsapp || '',
        instagram: trainer.instagram || ''
      });
    } else {
      setEditingTrainer(null);
      setFormData(initialTrainerForm);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.photo_url || !formData.location || !formData.bio || !formData.specialties) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    const payload = {
      ...formData,
      specialties: formData.specialties.split(',').map(s => s.trim()).filter(Boolean)
    };

    try {
      if (editingTrainer) {
        await axios.put(`${API_URL}/trainers/${editingTrainer.id}`, payload);
        toast.success('Trainer updated successfully');
      } else {
        await axios.post(`${API_URL}/trainers`, payload);
        toast.success('Trainer added successfully');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Operation failed';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTrainer = async (trainerId) => {
    if (!window.confirm('Are you sure you want to delete this trainer?')) return;

    try {
      await axios.delete(`${API_URL}/trainers/${trainerId}`);
      toast.success('Trainer deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete trainer');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      await axios.delete(`${API_URL}/reviews/${reviewId}`);
      toast.success('Review deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Header />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="admin-page">
      <Header />
      
      <main className="pt-24 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-primary/20">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter uppercase text-white">
                  Admin Dashboard
                </h1>
                <p className="text-gray-400">Manage trainers, payments and reviews</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleProcessPayouts}
                disabled={processingPayouts}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="process-payouts"
              >
                {processingPayouts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                Process Payouts
              </Button>
              <Button
                onClick={handleCreateDemoAccount}
                disabled={creatingDemo}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="create-demo"
              >
                {creatingDemo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Create Demo Account
              </Button>
              <Button
                variant="outline"
                onClick={handleClearTestData}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="clear-test-data"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Data
              </Button>
            </div>
          </div>

          {/* Payout Status Summary */}
          {payoutStatus && (
            <div className="bg-[#141414] rounded-2xl p-4 border border-white/5 mb-6 animate-fade-in-up">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Banknote className="w-5 h-5 text-green-400" />
                Automatic Payout Status
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {Object.entries(payoutStatus.status_summary || {}).map(([status, data]) => (
                  <div key={status} className="text-center">
                    <Badge className={`mb-1 ${
                      status === 'paid' ? 'bg-green-500/20 text-green-400' :
                      status === 'eligible' ? 'bg-blue-500/20 text-blue-400' :
                      status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                      status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {status}
                    </Badge>
                    <p className="text-white font-bold">{data.count}</p>
                    <p className="text-gray-400 text-xs">£{data.total?.toFixed(2) || '0.00'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="payments" className="animate-fade-in-up animation-delay-100">
            <TabsList className="bg-[#141414] border border-white/5 mb-6 flex-wrap">
              <TabsTrigger value="payments" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <PoundSterling className="w-4 h-4 mr-2" />
                Payments
              </TabsTrigger>
              <TabsTrigger value="disputes" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <Scale className="w-4 h-4 mr-2" />
                Disputes ({disputes.filter(d => d.status !== 'resolved').length})
              </TabsTrigger>
              <TabsTrigger value="lead-unlocks" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <Unlock className="w-4 h-4 mr-2" />
                Lead Unlocks
              </TabsTrigger>
              <TabsTrigger value="pending" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <Clock className="w-4 h-4 mr-2" />
                Pending ({pendingTrainers.length})
              </TabsTrigger>
              <TabsTrigger value="trainers" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" />
                Trainers ({trainers.length})
              </TabsTrigger>
              <TabsTrigger value="reviews" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                <MessageSquare className="w-4 h-4 mr-2" />
                Reviews ({reviews.length})
              </TabsTrigger>
            </TabsList>

            {/* Payments Tab */}
            <TabsContent value="payments">
              <div className="space-y-6">
                {/* Summary Cards */}
                {paymentSummary && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-white">£{paymentSummary.total_revenue?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Platform Commission</p>
                      <p className="text-3xl font-bold text-primary">£{paymentSummary.total_commission?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Total Transactions</p>
                      <p className="text-3xl font-bold text-white">{paymentSummary.total_transactions || 0}</p>
                    </div>
                  </div>
                )}

                {/* Transactions Table */}
                <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                  <div className="p-4 border-b border-white/5 flex items-center justify-between">
                    <div>
                      <h2 className="font-semibold text-white">Recent Transactions</h2>
                      <p className="text-sm text-gray-400">All payment activity</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/5 hover:bg-transparent">
                          <TableHead className="text-gray-400">Type</TableHead>
                          <TableHead className="text-gray-400">Amount</TableHead>
                          <TableHead className="text-gray-400">Commission</TableHead>
                          <TableHead className="text-gray-400">Payer</TableHead>
                          <TableHead className="text-gray-400">Status</TableHead>
                          <TableHead className="text-gray-400">Date</TableHead>
                          <TableHead className="text-gray-400 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((tx) => (
                          <TableRow key={tx.id} className="border-white/5 hover:bg-white/5">
                            <TableCell>
                              <Badge variant="secondary" className="bg-white/5 capitalize">
                                {tx.payment_type.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-white font-semibold">£{tx.amount?.toFixed(2)}</TableCell>
                            <TableCell className="text-primary">£{tx.commission?.toFixed(2)}</TableCell>
                            <TableCell className="text-gray-400 text-sm">{tx.payer_email}</TableCell>
                            <TableCell>
                              <Badge className={
                                tx.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                tx.status === 'refunded' ? 'bg-yellow-500/20 text-yellow-400' :
                                tx.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }>
                                {tx.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-gray-500 text-sm">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              {tx.status === 'completed' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRefund(tx.id)}
                                  className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10"
                                  data-testid={`refund-${tx.id}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-1" />
                                  Refund
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {transactions.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                              No transactions yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Disputes Tab */}
            <TabsContent value="disputes">
              <div className="space-y-6">
                {/* Attendance Summary */}
                {sessionAttendance && (
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Completed Sessions</p>
                      <p className="text-3xl font-bold text-green-400">
                        {sessionAttendance.status_summary?.completed?.count || 0}
                      </p>
                    </div>
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">User No-Shows</p>
                      <p className="text-3xl font-bold text-orange-400">
                        {sessionAttendance.status_summary?.user_no_show?.count || 0}
                      </p>
                    </div>
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Trainer No-Shows</p>
                      <p className="text-3xl font-bold text-red-400">
                        {sessionAttendance.status_summary?.trainer_missed?.count || 0}
                      </p>
                    </div>
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Active Disputes</p>
                      <p className="text-3xl font-bold text-yellow-400">
                        {disputes.filter(d => d.status !== 'resolved').length}
                      </p>
                    </div>
                  </div>
                )}

                {/* Disputes List */}
                <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                  <div className="p-4 border-b border-white/5">
                    <h2 className="font-semibold text-white">Session Disputes</h2>
                    <p className="text-sm text-gray-400">Review and resolve user/trainer disputes</p>
                  </div>

                  {disputes.length === 0 ? (
                    <div className="p-8 text-center">
                      <Scale className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">No disputes to review</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {disputes.map((dispute) => (
                        <div key={dispute.id} className="p-4 hover:bg-white/5 transition-colors">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl ${
                              dispute.status === 'resolved' ? 'bg-green-500/20' :
                              dispute.status === 'pending_admin' ? 'bg-yellow-500/20' :
                              'bg-orange-500/20'
                            }`}>
                              <Scale className={`w-5 h-5 ${
                                dispute.status === 'resolved' ? 'text-green-400' :
                                dispute.status === 'pending_admin' ? 'text-yellow-400' :
                                'text-orange-400'
                              }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-white">
                                  {dispute.booking?.user_name} vs {dispute.booking?.trainer_name}
                                </h3>
                                <Badge className={
                                  dispute.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                                  dispute.status === 'pending_admin' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-orange-500/20 text-orange-400'
                                }>
                                  {dispute.status?.replace(/_/g, ' ')}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-400">
                                {dispute.booking?.session_type?.replace('session_', '').replace('min', ' min')} session • £{dispute.booking?.amount?.toFixed(2)}
                              </p>
                              
                              {/* User's reason */}
                              <div className="mt-3 bg-[#1A1A1A] rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">User's Complaint:</p>
                                <p className="text-sm text-gray-300">{dispute.user_reason || 'No reason provided'}</p>
                              </div>
                              
                              {/* Trainer's response */}
                              {dispute.trainer_response && (
                                <div className="mt-2 bg-[#1A1A1A] rounded-lg p-3">
                                  <p className="text-xs text-gray-500 mb-1">Trainer's Response:</p>
                                  <p className="text-sm text-gray-300">{dispute.trainer_response}</p>
                                </div>
                              )}
                              
                              {/* Admin resolution */}
                              {dispute.admin_resolution && (
                                <div className="mt-2 bg-green-500/10 rounded-lg p-3">
                                  <p className="text-xs text-green-400 mb-1">Resolution:</p>
                                  <p className="text-sm text-green-300">{dispute.admin_resolution?.replace(/_/g, ' ')}</p>
                                  {dispute.admin_notes && <p className="text-xs text-gray-400 mt-1">{dispute.admin_notes}</p>}
                                </div>
                              )}
                              
                              <p className="text-xs text-gray-500 mt-2">
                                Disputed: {new Date(dispute.created_at).toLocaleString()}
                              </p>
                            </div>
                            
                            {/* Action buttons */}
                            {dispute.status === 'pending_admin' && (
                              <div className="flex flex-col gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleResolveDispute(dispute.id, 'no_refund')}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                  data-testid={`resolve-no-refund-${dispute.id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  No Refund
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleResolveDispute(dispute.id, 'user_refund_50')}
                                  className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
                                  data-testid={`resolve-50-refund-${dispute.id}`}
                                >
                                  50% Refund
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleResolveDispute(dispute.id, 'user_refund_100')}
                                  className="bg-red-600 hover:bg-red-700 text-white text-xs"
                                  data-testid={`resolve-100-refund-${dispute.id}`}
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Full Refund
                                </Button>
                              </div>
                            )}
                            
                            {dispute.status === 'pending_trainer_response' && (
                              <Badge className="bg-orange-500/20 text-orange-400">
                                Awaiting Trainer
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Lead Unlocks Tab */}
            <TabsContent value="lead-unlocks">
              <div className="space-y-6">
                {/* Admin Unlock Button */}
                <div className="flex justify-end">
                  <Dialog open={isAdminUnlockDialogOpen} onOpenChange={(open) => {
                    setIsAdminUnlockDialogOpen(open);
                    if (open) fetchTrainersAndUsersForUnlock();
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary text-white">
                        <Unlock className="w-4 h-4 mr-2" />
                        Admin Unlock (Bypass £4)
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#141414] border-white/10 text-white">
                      <DialogHeader>
                        <DialogTitle>Admin Unlock - Bypass £4 Fee</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label className="text-gray-400">Select Trainer</Label>
                          <select
                            value={adminUnlockForm.trainer_id}
                            onChange={(e) => setAdminUnlockForm(prev => ({ ...prev, trainer_id: e.target.value }))}
                            className="w-full h-10 px-3 bg-[#1A1A1A] border border-white/10 rounded-md text-white mt-1"
                          >
                            <option value="">-- Select Trainer --</option>
                            {trainersForUnlock.map(t => (
                              <option key={t.id} value={t.id}>{t.name} ({t.email})</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-gray-400">Select Client (User)</Label>
                          <select
                            value={adminUnlockForm.user_id}
                            onChange={(e) => setAdminUnlockForm(prev => ({ ...prev, user_id: e.target.value }))}
                            className="w-full h-10 px-3 bg-[#1A1A1A] border border-white/10 rounded-md text-white mt-1"
                          >
                            <option value="">-- Select User --</option>
                            {usersForUnlock.map(u => (
                              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-gray-400">Reason (optional)</Label>
                          <Input
                            value={adminUnlockForm.reason}
                            onChange={(e) => setAdminUnlockForm(prev => ({ ...prev, reason: e.target.value }))}
                            placeholder="e.g., Promotional, Dispute resolution"
                            className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                          />
                        </div>
                        <Button
                          onClick={handleAdminUnlock}
                          disabled={submitting}
                          className="w-full bg-primary text-white"
                        >
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
                          Unlock Client (Free)
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Summary Stats */}
                {leadUnlocks?.summary && (
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Total Unlocks</p>
                      <p className="text-3xl font-bold text-white">{leadUnlocks.summary.total_unlocks}</p>
                    </div>
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Paid Unlocks</p>
                      <p className="text-3xl font-bold text-primary">{leadUnlocks.summary.paid_unlocks}</p>
                    </div>
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Premium (Free)</p>
                      <p className="text-3xl font-bold text-yellow-400">{leadUnlocks.summary.premium_free}</p>
                    </div>
                    <div className="bg-[#141414] rounded-2xl p-6 border border-white/5">
                      <p className="text-gray-400 text-sm mb-1">Unlock Revenue</p>
                      <p className="text-3xl font-bold text-green-400">£{leadUnlocks.summary.total_revenue?.toFixed(2)}</p>
                    </div>
                  </div>
                )}

                {/* Unlock History */}
                <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                  <div className="p-4 border-b border-white/5">
                    <h2 className="font-semibold text-white">Lead Unlock Audit Log</h2>
                    <p className="text-sm text-gray-400">Track all lead unlocks for revenue protection</p>
                  </div>

                  {!leadUnlocks?.unlocks?.length ? (
                    <div className="p-8 text-center">
                      <Unlock className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">No lead unlocks yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/5 hover:bg-transparent">
                            <TableHead className="text-gray-400">Trainer</TableHead>
                            <TableHead className="text-gray-400">User</TableHead>
                            <TableHead className="text-gray-400">Type</TableHead>
                            <TableHead className="text-gray-400">Amount</TableHead>
                            <TableHead className="text-gray-400">Status</TableHead>
                            <TableHead className="text-gray-400">Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {leadUnlocks.unlocks.map((unlock) => (
                            <TableRow key={unlock.id} className="border-white/5 hover:bg-white/5">
                              <TableCell>
                                <div>
                                  <p className="text-white font-medium">{unlock.trainer_name}</p>
                                  <p className="text-xs text-gray-500">{unlock.trainer_email}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="text-white">{unlock.user_name}</p>
                                  <p className="text-xs text-gray-500">{unlock.user_email}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                {unlock.unlock_type === 'premium_free' ? (
                                  <Badge className="bg-yellow-500/20 text-yellow-400">Premium</Badge>
                                ) : unlock.unlock_type === 'admin_bypass' ? (
                                  <Badge className="bg-purple-500/20 text-purple-400">Admin</Badge>
                                ) : (
                                  <Badge className="bg-primary/20 text-primary">Paid</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-primary font-semibold">
                                {unlock.amount > 0 ? `£${unlock.amount.toFixed(2)}` : 'Free'}
                              </TableCell>
                              <TableCell>
                                {unlock.status === 'completed' || !unlock.status ? (
                                  <Badge className="bg-green-500/20 text-green-400">Completed</Badge>
                                ) : (
                                  <Badge className="bg-yellow-500/20 text-yellow-400">{unlock.status}</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-gray-500 text-sm">
                                {new Date(unlock.created_at).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Pending Trainers Tab */}
            <TabsContent value="pending">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h2 className="font-semibold text-white">Pending Applications</h2>
                  <p className="text-sm text-gray-400">Review and approve trainer applications</p>
                </div>

                {pendingTrainers.length === 0 ? (
                  <div className="p-8 text-center">
                    <Clock className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No pending applications</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {pendingTrainers.map((application) => (
                      <div key={application.id} className="p-4 hover:bg-white/5 transition-colors">
                        <div className="flex items-start gap-4">
                          <img
                            src={getImageUrl(application.photo_url)}
                            alt={application.name}
                            className="w-16 h-16 rounded-xl object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white">{application.name}</h3>
                            <p className="text-sm text-gray-400">{application.location}</p>
                            <p className="text-sm text-gray-500">{application.email}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {application.specialties.map((s, i) => (
                                <Badge key={i} variant="secondary" className="bg-white/5 text-xs">
                                  {s}
                                </Badge>
                              ))}
                            </div>
                            <p className="text-sm text-gray-400 mt-2 line-clamp-2">{application.bio}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                              {application.whatsapp && <span>WhatsApp: {application.whatsapp}</span>}
                              {application.instagram && <span>IG: @{application.instagram}</span>}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveTrainer(application.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-${application.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectTrainer(application.id)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              data-testid={`reject-${application.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Trainers Tab */}
            <TabsContent value="trainers">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <h2 className="font-semibold text-white">All Trainers</h2>
                  <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => handleOpenDialog()}
                        className="bg-primary text-white hover:bg-primary/90"
                        data-testid="add-trainer-button"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Trainer
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#141414] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-heading text-xl uppercase">
                          {editingTrainer ? 'Edit Trainer' : 'Add New Trainer'}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label className="text-gray-400">Name *</Label>
                          <Input
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="bg-[#1A1A1A] border-transparent text-white"
                            data-testid="trainer-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400">Photo URL *</Label>
                          <Input
                            name="photo_url"
                            value={formData.photo_url}
                            onChange={handleInputChange}
                            className="bg-[#1A1A1A] border-transparent text-white"
                            data-testid="trainer-photo-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400">Location *</Label>
                          <Input
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            className="bg-[#1A1A1A] border-transparent text-white"
                            data-testid="trainer-location-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400">Bio *</Label>
                          <Textarea
                            name="bio"
                            value={formData.bio}
                            onChange={handleInputChange}
                            className="bg-[#1A1A1A] border-transparent text-white min-h-[100px]"
                            data-testid="trainer-bio-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400">Specialties * (comma separated)</Label>
                          <Input
                            name="specialties"
                            value={formData.specialties}
                            onChange={handleInputChange}
                            placeholder="Strength, HIIT, Yoga"
                            className="bg-[#1A1A1A] border-transparent text-white"
                            data-testid="trainer-specialties-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400">WhatsApp Number</Label>
                          <Input
                            name="whatsapp"
                            value={formData.whatsapp}
                            onChange={handleInputChange}
                            placeholder="+1234567890"
                            className="bg-[#1A1A1A] border-transparent text-white"
                            data-testid="trainer-whatsapp-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400">Instagram Username</Label>
                          <Input
                            name="instagram"
                            value={formData.instagram}
                            onChange={handleInputChange}
                            placeholder="username"
                            className="bg-[#1A1A1A] border-transparent text-white"
                            data-testid="trainer-instagram-input"
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={submitting}
                          className="w-full bg-primary text-white font-bold uppercase tracking-wider"
                          data-testid="save-trainer-button"
                        >
                          {submitting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : editingTrainer ? (
                            'Update Trainer'
                          ) : (
                            'Add Trainer'
                          )}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-gray-400">Trainer</TableHead>
                        <TableHead className="text-gray-400">Location</TableHead>
                        <TableHead className="text-gray-400">Specialties</TableHead>
                        <TableHead className="text-gray-400">Rating</TableHead>
                        <TableHead className="text-gray-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trainers.map((trainer) => (
                        <TableRow key={trainer.id} className="border-white/5 hover:bg-white/5">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <img
                                src={getImageUrl(trainer.photo_url)}
                                alt={trainer.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                              <span className="font-medium text-white">{trainer.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-400">{trainer.location}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {trainer.specialties.slice(0, 2).map((s, i) => (
                                <Badge key={i} variant="secondary" className="bg-white/5 text-xs">
                                  {s}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              <span className="text-white">{trainer.rating.toFixed(1)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDialog(trainer)}
                                className="text-gray-400 hover:text-white hover:bg-white/10"
                                data-testid={`edit-trainer-${trainer.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteTrainer(trainer.id)}
                                className="text-gray-400 hover:text-red-400 hover:bg-red-400/10"
                                data-testid={`delete-trainer-${trainer.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h2 className="font-semibold text-white">All Reviews</h2>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-gray-400">User</TableHead>
                        <TableHead className="text-gray-400">Rating</TableHead>
                        <TableHead className="text-gray-400">Review</TableHead>
                        <TableHead className="text-gray-400">Date</TableHead>
                        <TableHead className="text-gray-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reviews.map((review) => (
                        <TableRow key={review.id} className="border-white/5 hover:bg-white/5">
                          <TableCell className="text-white">{review.user_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              <span className="text-white">{review.rating}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-400 max-w-xs truncate">
                            {review.text}
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {new Date(review.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteReview(review.id)}
                              className="text-gray-400 hover:text-red-400 hover:bg-red-400/10"
                              data-testid={`delete-review-${review.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {reviews.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                            No reviews yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
